import React, { useEffect, useState } from 'react';
import { ResourceField, ResourceFormPage } from '../../components/resource/ResourceFormPage';
import { CompraMadeira, ItemCompraMadeira } from '../../types/aquisicao';
import {
  comprasMadeiraService,
  fornecedoresService,
  tiposMadeiraService,
  unidadesMedidaService,
} from '../../services';
import { makeOptionLoader } from '../../utils/optionLoaders';

interface SelectOption {
  value: number;
  label: string;
}

const ItensCompraField: React.FC<{
  value: unknown;
  onChange: (val: ItemCompraMadeira[]) => void;
}> = ({ value, onChange }) => {
  const itens = Array.isArray(value) ? (value as ItemCompraMadeira[]) : [];
  const [madeiraOptions, setMadeiraOptions] = useState<SelectOption[]>([]);
  const [unidadeOptions, setUnidadeOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    tiposMadeiraService
      .list({ page_size: 100 })
      .then((res) =>
        setMadeiraOptions(
          res.results.map((item: any) => ({
            value: item.id,
            label: `${item.codigo ?? ''} ${item.descricao ?? ''}`.trim(),
          })),
        ),
      )
      .catch((err) => console.error('Erro ao carregar tipos de madeira', err));

    unidadesMedidaService
      .list({ page_size: 100 })
      .then((res) =>
        setUnidadeOptions(
          res.results.map((item: any) => ({
            value: item.id,
            label: `${item.codigo ?? ''} ${item.descricao ?? ''}`.trim(),
          })),
        ),
      )
      .catch((err) => console.error('Erro ao carregar unidades de medida', err));
  }, []);

  const handleUpdate = (index: number, patch: Partial<ItemCompraMadeira>) => {
    const next = itens.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    onChange(next);
  };

  const handleAdd = () => {
    onChange([
      ...itens,
      {
        compra: 0,
        tipo_madeira: undefined as unknown as number,
        quantidade: undefined as unknown as number,
        unidade_medida: undefined as unknown as number,
        valor_unitario: undefined as unknown as number,
        observacoes: '',
        lote: null,
      } as unknown as ItemCompraMadeira,
    ]);
  };

  const handleRemove = (index: number) => {
    onChange(itens.filter((_, idx) => idx !== index));
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <strong>Itens</strong>
          <div className="text-muted small">Adicione um ou mais itens da compra.</div>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAdd}>
          Adicionar item
        </button>
      </div>

      {itens.length === 0 && <div className="text-muted">Nenhum item adicionado.</div>}

      {itens.map((item, index) => (
        <div key={index} className="border rounded p-3 d-flex flex-column gap-2">
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label">Madeira</label>
              <select
                className="form-select"
                value={item.tipo_madeira ?? ''}
                onChange={(e) =>
                  handleUpdate(index, {
                    tipo_madeira: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                required
              >
                <option value="">Selecione</option>
                {madeiraOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Quantidade</label>
              <input
                type="number"
                className="form-control"
                value={(item.quantidade as number | null) ?? ''}
                onChange={(e) =>
                  handleUpdate(index, {
                    quantidade: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Unidade</label>
              <select
                className="form-select"
                value={item.unidade_medida ?? ''}
                onChange={(e) =>
                  handleUpdate(index, {
                    unidade_medida: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                required
              >
                <option value="">Selecione</option>
                {unidadeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Valor unitário</label>
              <input
                type="number"
                className="form-control"
                value={(item.valor_unitario as number | null) ?? ''}
                onChange={(e) =>
                  handleUpdate(index, {
                    valor_unitario: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Lote (opcional)</label>
              <input
                type="number"
                className="form-control"
                value={(item.lote as number | null) ?? ''}
                onChange={(e) =>
                  handleUpdate(index, {
                    lote: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </div>
          </div>
          <div className="d-flex gap-2 align-items-start">
            <div className="flex-grow-1">
              <label className="form-label">Observações</label>
              <textarea
                className="form-control"
                rows={2}
                value={(item.observacoes as string) ?? ''}
                onChange={(e) => handleUpdate(index, { observacoes: e.target.value } as any)}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => handleRemove(index)}
              aria-label="Remover item"
            >
              Remover
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const fields: ResourceField<CompraMadeira>[] = [
  {
    name: 'entidade',
    label: 'Fornecedor',
    type: 'async-select',
    valueType: 'number',
    loadOptions: makeOptionLoader(
      fornecedoresService.list,
      (item: any) => item.razao_social ?? item.nome_fantasia ?? String(item.id),
    ),
    required: true,
  },
  { name: 'data_compra', label: 'Data da compra', type: 'date', required: true },
  {
    name: 'apta_para_estoque',
    label: 'Apta para estoque',
    render: ({ value, onChange }) => (
      <div className="d-flex flex-column">
        <label className="form-label">Apta para estoque</label>
        <select
          className="form-select"
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) =>
            onChange(e.target.value === '' ? null : e.target.value === 'true')
          }
          required
        >
          <option value="">Selecione</option>
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </select>
      </div>
    ),
  },
  { name: 'observacoes', label: 'Observações', type: 'textarea' },
  {
    name: 'itens',
    label: 'Itens',
    render: ({ value, onChange }) => (
      <ItensCompraField value={value} onChange={(val) => onChange(val ?? [])} />
    ),
  },
];

export const ComprasMadeiraFormPage: React.FC = () => (
  <ResourceFormPage
    title="Compra de madeira (entrada)"
    description="Cadastre a entrada de madeira com seus itens."
    fields={fields}
    service={comprasMadeiraService}
    redirectPath="/admin/aquisicao/compras-madeira"
    cancelPath="/admin/aquisicao/compras-madeira"
  />
);
